/**
 * StartTrial page — /start (alias: /trial)
 *
 * Focused trial activation page for warm leads who already have an access code.
 * Sales reps send this link after a demo call.
 *
 * Public route — no auth required.
 */

import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUsageVisibility } from '../hooks/useUsageVisibility';
import ProfileImage from '../components/ProfileImage';
import NotificationsBell from '../components/NotificationsBell';
import MobileNotifications from '../components/MobileNotifications';
import SeoHead from '../components/SeoHead';
import GlobalFooter from '../components/GlobalFooter';
import {
  AccountQuestionPanel,
  RedemptionOutcomePanel,
  RedeemProgressPanel,
  type RedemptionOutcome,
} from '../components/redeem/RedemptionPanels';
import { FocusedRedeemShell } from '../components/redeem/FocusedRedeemShell';
import { prefetchOnboardingRecommendations } from '../services/onboardingRecsPrefetch';
import { JourneyStepper } from '../components/onboarding/JourneyStepper';
// LP-R4 (2026-08-16): both the redeem call and the pre-auth status probe run
// through the shared client — one error map for every surface (the inline
// fetch's partial map made CODE_EXPIRED / CODE_AMOUNT_INVALID unreachable).
import {
  checkAccessCode as checkAccessCodeRequest,
  redeemAccessCode as redeemAccessCodeRequest,
} from '../services/accessCodeClient';

// ============================================================================
// Animations
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const inputPulse = keyframes`
  0%, 100% { border-color: #E0DED8; }
  50% { border-color: var(--brand-gold); box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.15); }
`;

// ============================================================================
// Navigation (matches Pricing.tsx pattern)
// ============================================================================

const HomeHeader = styled.header`
  position: fixed;
  top: 1rem;
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 100;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderIconLink = styled(Link)`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;

  &:hover {
    color: var(--brand-charcoal);
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const HeaderIconButton = styled.button`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  background: none;
  border: none;
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;

  &:hover {
    color: var(--brand-charcoal);
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg { opacity: 1; }
`;

const MobileMenuToggle = styled.button`
  display: none;
  position: fixed;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 8px;
  z-index: 1100;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 5px;
  padding: 10px;
  transition: all 0.3s ease;

  &:hover { box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2); }

  @media (max-width: 768px) { display: flex; }

  @media (max-width: 480px) {
    width: 38px;
    height: 38px;
    top: 0.75rem;
    right: 0.75rem;
  }
`;

const MobileMenuIcon = styled.div<{ isOpen: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;

  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: var(--brand-black);
    border-radius: 2px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: .25s ease-in-out;

    &:nth-child(1) {
      top: ${props => props.isOpen ? '9px' : '4px'};
      transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0)'};
    }
    &:nth-child(2) {
      top: 9px;
      opacity: ${props => props.isOpen ? '0' : '1'};
    }
    &:nth-child(3) {
      top: ${props => props.isOpen ? '9px' : '14px'};
      transform: ${props => props.isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
    }
  }
`;

const MobileMenuDropdown = styled.div<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 4rem;
  right: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  padding: 1rem 0;
  z-index: 200000;
  flex-direction: column;
  width: 200px;
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.95)'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  transform-origin: top right;
  transition: all 0.25s ease-out;

  @media (max-width: 768px) { display: flex; }

  @media (max-width: 480px) {
    width: 180px;
    top: 3.5rem;
    right: 0.75rem;
    padding: 0.75rem 0;
  }
`;

const MobileMenuLink = styled(Link)`
  color: var(--brand-text-secondary);
  text-decoration: none;
  padding: 0.85rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  font-size: 0.95rem;

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #000;
    svg { opacity: 1; }
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.9rem;
    svg { width: 18px; height: 18px; }
  }
`;

const MobileMenuButton = styled.button`
  color: var(--brand-text-secondary);
  text-decoration: none;
  padding: 0.85rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: 0.95rem;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #000;
    svg { opacity: 1; }
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.9rem;
    svg { width: 18px; height: 18px; }
  }
`;

// ============================================================================
// Layout
// ============================================================================

const Page = styled.main`
  background: var(--brand-canvas);
  min-height: 100vh;
  padding: 0 0 5rem;
`;

const Hero = styled.section`
  max-width: 700px;
  margin: 0 auto;
  padding: 6rem 1.5rem 3rem;
  text-align: center;
  animation: ${fadeIn} 0.45s ease both;

  @media (min-width: 768px) {
    padding: 7rem 2rem 3.5rem;
  }
`;

const Eyebrow = styled.p`
  font-size: 0.7rem;
  text-transform: lowercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: #AAA;
  margin-bottom: 1rem;
`;

const HeroTitle = styled.h1`
  font-family: 'Space Grotesk', 'Inter', -apple-system, sans-serif;
  font-size: 2.4rem;
  font-weight: 400;
  letter-spacing: -0.03em;
  color: var(--brand-text);
  line-height: 1.15;
  margin-bottom: 1rem;

  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const HeroSub = styled.p`
  font-size: 1rem;
  color: #666;
  line-height: 1.7;
  max-width: 480px;
  margin: 0 auto;
`;

// ============================================================================
// Code Input Section
// ============================================================================

const CodeSection = styled.section`
  max-width: 520px;
  margin: 2.5rem auto 0;
  padding: 0 1.25rem;
  animation: ${fadeIn} 0.5s 0.08s ease both;
`;

const BigCodeInputRow = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: stretch;
  /* Phone widths: stack the input and button instead of overflowing the
     viewport (2026-08-02 viewport audit: +103px overflow at 320px). */
  @media (max-width: 560px) {
    flex-direction: column;
  }
`;

const BigCodeInput = styled.input<{ $pulsed?: boolean }>`
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  width: 100%;
  border: 2px solid #E0DED8;
  border-radius: 1rem;
  padding: 1rem 1.25rem;
  font-size: 1.1rem;
  font-family: 'Space Grotesk', 'Inter', monospace;
  letter-spacing: 0.05em;
  text-transform: lowercase;
  background: #fff;
  color: var(--brand-text);
  outline: none;
  transition: border-color 0.15s ease;
  ${props => props.$pulsed ? css`animation: ${inputPulse} 1s ease 0.4s 2;` : ''}

  &::placeholder { color: #BBB; text-transform: none; letter-spacing: 0; }
  &:focus { border-color: var(--brand-text); }
`;

const BigCodeButton = styled.button`
  background: var(--brand-text);
  color: #fff;
  border: none;
  border-radius: 1rem;
  padding: 1rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease;

  &:hover { background: #2a2a2a; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CodeError = styled.div`
  margin-top: 0.6rem;
  color: #B42318;
  font-size: 0.82rem;
  line-height: 1.5;
`;

const CodeSuccess = styled.div`
  margin-top: 0.6rem;
  color: #067647;
  font-size: 0.82rem;
  line-height: 1.5;
`;

// ============================================================================
// Trust Signals
// ============================================================================

const TrustRow = styled.div`
  max-width: 600px;
  margin: 2.5rem auto 0;
  padding: 0 1.25rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;
  animation: ${fadeIn} 0.5s 0.12s ease both;
`;

const TrustPill = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: #fff;
  border: 1px solid #E6E4DE;
  border-radius: 999px;
  padding: 0.45rem 0.9rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #444;
  white-space: nowrap;
`;

const TrustDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #27AE60;
  flex-shrink: 0;
`;

// ============================================================================
// Capabilities Grid
// ============================================================================

const CapSection = styled.section`
  max-width: 960px;
  margin: 4rem auto 0;
  padding: 0 1.25rem;
  animation: ${fadeIn} 0.5s 0.16s ease both;
`;

const CapHeading = styled.h2`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--brand-text);
  text-align: center;
  letter-spacing: -0.01em;
  margin-bottom: 1.5rem;
`;

const CapSubheading = styled.h3`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 0.85rem;
  font-weight: 500;
  color: #666;
  text-align: center;
  letter-spacing: -0.01em;
  margin: 2.5rem 0 1.25rem;
`;

const CapGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr 1fr;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const BuildableGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr 1fr;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const BuildableCard = styled.div`
  background: #FAFAFA;
  border: 1px dashed #DDD;
  border-radius: 1rem;
  padding: 1.25rem;
`;

const CapCard = styled.div`
  background: #fff;
  border: 1px solid #EBEBEA;
  border-radius: 1rem;
  padding: 1.25rem;
`;

const CapIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 0.6rem;
`;

const CapTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--brand-text);
  margin-bottom: 0.3rem;
`;

const CapDesc = styled.div`
  font-size: 0.78rem;
  color: #888;
  line-height: 1.5;
`;

// ============================================================================
// Footer
// ============================================================================

const FooterNote = styled.div`
  max-width: 560px;
  margin: 3.5rem auto 0;
  padding: 0 1.25rem;
  text-align: center;
  font-size: 0.8rem;
  color: #AAA;
  line-height: 1.7;

  a {
    color: #888;
    text-decoration: underline;
    &:hover { color: var(--brand-text); }
  }
`;

// ============================================================================
// Component
// ============================================================================

// ---------------------------------------------------------------------------
// What Prest0 can do TODAY — real, shipping capabilities
// ---------------------------------------------------------------------------
const LIVE_CAPABILITIES = [
  {
    icon: '⚖️',
    title: 'Deep Research',
    desc: 'Case law, market data, regulations, and any hard question — researched and summarized in minutes, not hours.',
  },
  {
    icon: '📝',
    title: 'Document Drafting',
    desc: 'Motions, demand letters, contracts, NDAs, briefs, and memos — drafted in your voice, on demand.',
  },
  {
    icon: '📄',
    title: 'Document Reading & Analysis',
    desc: 'Upload any PDF, DOCX, or contract. Prest0 reads it end-to-end and answers questions about it.',
  },
  {
    icon: '🌐',
    title: 'Bilingual AI (EN ↔ ES)',
    desc: 'Full native English and Spanish support — documents, client messages, and forms translated instantly.',
  },
  {
    icon: '📞',
    title: 'AI Voice Calls',
    desc: 'Prest0 makes and receives phone calls on your behalf. Conversational, natural, with action follow-up.',
  },
  {
    icon: '💬',
    title: 'SMS & Text Messaging',
    desc: 'Send and receive text messages to clients, with delivery tracking and automated follow-ups.',
  },
  {
    icon: '📧',
    title: 'Email Management',
    desc: 'Read, draft, and send emails from your team\'s inbox. Triage urgent messages automatically.',
  },
  {
    icon: '📅',
    title: 'Calendar & Scheduling',
    desc: 'Manage appointments, court dates, and deadlines. Get proactive reminders before things are urgent.',
  },
  {
    icon: '🔍',
    title: 'Web & Market Research',
    desc: 'Real-time internet research — opposing counsel backgrounds, company filings, news, public records.',
  },
  {
    icon: '📋',
    title: 'Form Filling (USCIS & More)',
    desc: 'Automatically populate I-589s, I-130s, and other government forms from client intake data.',
  },
  {
    icon: '🎬',
    title: 'Video Script Generation',
    desc: 'Educational and client-communication video scripts for your firm — ready for production.',
  },
  {
    icon: '📊',
    title: 'Spreadsheet & Data Work',
    desc: 'Create, read, and manipulate Google Sheets and Excel files. Reports, trackers, billing summaries.',
  },
  {
    icon: '🧠',
    title: 'Institutional Memory',
    desc: 'Every case teaches the system. Past matters, strategies, and outcomes inform future work.',
  },
  {
    icon: '📱',
    title: 'Client Communication Hub',
    desc: 'Unified inbox across email, SMS, and voice — keep every client touchpoint in one place.',
  },
  {
    icon: '🔗',
    title: '1,000+ Integrations',
    desc: 'Connect Clio, QuickBooks, Salesforce, Google Workspace, Slack, and more out of the box.',
  },
  {
    icon: '🔒',
    title: 'Privacy & Privilege Protection',
    desc: 'Client data stays yours. Encrypted, never shared, deleted on demand. Built for attorney-client privilege.',
  },
];

// ---------------------------------------------------------------------------
// What firms can BUILD on Prest0 — future / customizable
// ---------------------------------------------------------------------------
const BUILDABLE_CAPABILITIES = [
  {
    icon: '🤖',
    title: 'Custom Specialist Agents',
    desc: 'Build specialized AI agents for legal, finance, operations — any domain you work in.',
  },
  {
    icon: '📱',
    title: 'Client Self-Service Portal',
    desc: 'Give clients their own dashboard to check status, upload documents, and message your team 24/7.',
  },
  {
    icon: '⚙️',
    title: 'Automated Workflows',
    desc: 'Trigger sequences when events happen — new client signs up, deadline approaches, payment received.',
  },
  {
    icon: '📈',
    title: 'Revenue & Operations Analytics',
    desc: 'Track collections, utilization rates, project profitability, and pipeline health in real time.',
  },
  {
    icon: '🏢',
    title: 'Multi-Office Coordination',
    desc: 'Share knowledge and workflows across locations. One system for the whole team.',
  },
  {
    icon: '📥',
    title: 'Client Intake Automation',
    desc: 'Intake forms, document requests, conflict checks, and follow-ups — handled end to end by AI.',
  },
  {
    icon: '🎓',
    title: 'Training & Education Content',
    desc: 'Auto-generate training materials and onboarding docs for new team members.',
  },
  {
    icon: '🏛️',
    title: 'Competitive Intelligence',
    desc: 'Monitor what others in your market are doing, charging, and winning. Stay ahead.',
  },
];

const TRUST_SIGNALS = [
  { label: 'Invite-Only Access' },
  { label: 'No Credit Card' },
  { label: 'Enterprise Features' },
  { label: 'PRO Work Units Included' },
];

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
`;

const StartTrial: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const { canSeeWallet, canSeeActivity } = useUsageVisibility();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const activateButtonRef = useRef<HTMLButtonElement>(null);

  const [codeInput, setCodeInput] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeSuccess, setCodeSuccess] = useState(false);
  const [codeSuccessMessage, setCodeSuccessMessage] = useState('Code accepted! Setting up your evaluation workspace…');
  const [prefilledFromUrl, setPrefilledFromUrl] = useState(false);
  // Signed-out + valid unused code → ask whether they already have an account
  const [accountQuestionCode, setAccountQuestionCode] = useState<string | null>(null);
  // Signed-in wallet-credit / trial-extension confirmations (no onboarding)
  const [redeemOutcome, setRedeemOutcome] = useState<RedemptionOutcome | null>(null);

  // Pre-fill code from URL param; auto-focus activate button if code present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prefilledCode = params.get('code');
    if (prefilledCode) {
      setCodeInput(prefilledCode.toUpperCase());
      setPrefilledFromUrl(true);
      setTimeout(() => {
        activateButtonRef.current?.focus();
      }, 350);
    }
  }, [location.search]);

  const [returnedFromAuth, setReturnedFromAuth] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    if (code && isAuthenticated) {
      setReturnedFromAuth(true);
    }
  }, [isAuthenticated, location.search]);

  // Kill the marketing-page flash on auth return (2026-08-09 owner
  // directive): returnedFromAuth + the auto-redeem's codeLoading are set in
  // post-commit effects, so the FIRST paint after returning from /auth was
  // the full marketing page before the focused shell mounted. This boot flag
  // initializes synchronously from the same signals the auto-redeem effect
  // uses (?code= in the URL + the pending code it will consume), so the very
  // first commit already renders the shell. A safety timeout drops it if
  // auth never settles (abandoned flow revisited), so the shell can't stick.
  const [authReturnBoot, setAuthReturnBoot] = useState(() => {
    try {
      const hasCode = new URLSearchParams(window.location.search).has('code');
      return hasCode && Boolean(sessionStorage.getItem('pending_trial_code'));
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!authReturnBoot) return;
    const t = setTimeout(() => {
      if (!isAuthenticated) setAuthReturnBoot(false);
    }, 8000);
    return () => clearTimeout(t);
  }, [authReturnBoot, isAuthenticated]);
  // Hand off to the live redemption state the moment it starts — without
  // this, the boot arm would outlive a wallet_credit / trial_extension
  // redemption (codeSuccess never fires for those) and mask their outcome
  // panels behind a stuck "applying…" card.
  useEffect(() => {
    if (codeLoading && authReturnBoot) setAuthReturnBoot(false);
  }, [codeLoading, authReturnBoot]);

  // ── Auto-redeem: fires once when user returns from auth with a pending code ──
  // pending_trial_code is set in handleRedeemCode before navigating to /auth.
  // Cleared immediately on use so page refreshes don't re-trigger.
  useEffect(() => {
    if (!isAuthenticated) return;
    let pendingCode: string | null = null;
    try {
      pendingCode = sessionStorage.getItem('pending_trial_code');
      if (pendingCode) sessionStorage.removeItem('pending_trial_code');
    } catch {}
    if (!pendingCode) return;
    const code = pendingCode.toUpperCase();
    setCodeInput(code);
    redeemCode(code);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = () => {
    navigate('/auth', { state: { returnUrl: '/start' } });
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('button[data-mobile-toggle="true"]')
      ) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Core redemption logic (called after user is authenticated) ──
  // Shared-client contract: never throws — every failure (missing session,
  // network trouble, typed server errors incl. CODE_EXPIRED /
  // CODE_AMOUNT_INVALID, and the masked-hint CODE_REDEEMED_BY_OTHER) arrives
  // as a ready-to-render message.
  const redeemCode = async (code: string) => {
    setCodeLoading(true);
    setCodeError(null);

    try {
      const result = await redeemAccessCodeRequest(code);

      if (!result.success) {
        setCodeError(result.message);
        return;
      }

      // Success — route by redemption mode (see functions/src/accessCodeRedemption.ts)
      if (result.mode === 'wallet_credit') {
        // Active account: credit added to their wallet — confirmation panel,
        // no onboarding.
        setRedeemOutcome({ mode: 'wallet_credit', creditCents: result.creditAmountCents ?? 0 });
        return;
      }
      if (result.mode === 'trial_extension') {
        // Existing trial (active or expired): credit added + clock extended.
        setRedeemOutcome({
          mode: 'trial_extension',
          creditCents: result.creditAddedCents ?? 0,
          expiresAt: result.expiresAt ?? null,
        });
        return;
      }

      setCodeSuccessMessage(
        result.returning
          ? 'Welcome back! Taking you to your workspace…'
          : 'Code accepted! Setting up your evaluation workspace…'
      );
      setCodeSuccess(true);
      // Start the firm-aware recommendation generation NOW, in parallel with
      // the redirect + onboarding bootstrap — the profile page's fetch then
      // hits the warm server cache instead of waiting 10-15s (2026-08-04
      // owner feedback).
      prefetchOnboardingRecommendations();
      if (result.returning) {
        // Returning user — go straight to workspace
        setTimeout(() => navigate('/desktop'), 1200);
      } else {
        // New user — go through onboarding
        if (result.vmProvisioned) {
          setCodeSuccessMessage('Success! Provisioning your dedicated Prest0n VM. This will take a few seconds...');
          // Wait longer so they see the message before transitioning to onboarding
          setTimeout(() => navigate('/onboarding'), 3000);
        } else {
          setTimeout(() => navigate('/onboarding'), 1200);
        }
      }
    } finally {
      setCodeLoading(false);
    }
  };

  // ── Button handler: check code status, route to correct auth mode ──
  const handleRedeemCode = async () => {
    const trimmedCode = codeInput.trim().toUpperCase();
    if (!trimmedCode) {
      setCodeError('Please enter an access code.');
      return;
    }

    // If already authenticated, go straight to redemption
    if (isAuthenticated) {
      await redeemCode(trimmedCode);
      return;
    }

    // Not authenticated — check if code is new or already used
    await routeSignedOutCode(trimmedCode);
  };

  // Signed-out code routing — shared by the button handler and the one-click
  // invite-link auto-advance (checkAccessCode is READ-ONLY; a shared link can
  // never consume a code).
  const routeSignedOutCode = async (trimmedCode: string) => {
    setCodeLoading(true);
    setCodeError(null);

    try {
      // Shared client (never throws) — typed failures include the expiry
      // pre-check (an expired unused code errors here instead of funneling
      // the visitor into account creation toward a guaranteed dead end).
      const check = await checkAccessCodeRequest(trimmedCode);

      if (!check.success) {
        setCodeError(check.message);
        return;
      }

      const returnUrl = `/start?code=${encodeURIComponent(trimmedCode)}`;

      if (check.status === 'unused') {
        // Valid new code, signed out → first ask whether they already have an
        // account, so the code applies to the RIGHT account. Auto-redeem flag
        // is set at COMMIT time in the panel handlers (2026-08-02 review:
        // backing out must not leave a stale pending_trial_code).
        setAccountQuestionCode(trimmedCode);
      } else {
        // Already redeemed (attachedToExistingAccount) → send to login with a
        // NEUTRAL hint: the pre-auth probe no longer discloses a masked email
        // (LP-R5) — the hint appears only after auth, on the redeem-time
        // CODE_REDEEMED_BY_OTHER response. Committed: navigating now.
        try { sessionStorage.setItem('pending_trial_code', trimmedCode); } catch {}
        navigate('/auth', {
          state: {
            returnUrl,
            authMode: 'login',
            loginHint: 'This code is already linked to an account. Sign in to continue.',
          },
        });
      }
    } finally {
      setCodeLoading(false);
    }
  };

  // ── One-click invite links (2026-08-02) — same contract as /pricing:
  // signed-out visitors auto-advance into the focused account question;
  // signed-in users keep prefill + explicit apply; an in-flight auth-return
  // is owned by the auto-redeem effect above.
  const autoAdvancedRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const linkCode = (params.get('code') || '').trim().toUpperCase();
    if (!linkCode || autoAdvancedRef.current || isAuthenticated) return;
    let authReturnInFlight = false;
    try { authReturnInFlight = Boolean(sessionStorage.getItem('pending_trial_code')); } catch {}
    if (authReturnInFlight) return;
    const t = setTimeout(() => {
      autoAdvancedRef.current = true;
      void routeSignedOutCode(linkCode);
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isAuthenticated]);

  const handleCodeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleRedeemCode();
  };

  return (
    <Page>
      {/* Post-auth activation (2026-08-04 owner parity directive): invite
          LINKS must walk the byte-same journey as a code typed on /pricing.
          This step previously showed a dark marketing-styled lock screen
          here while /pricing showed the light focused-shell panel with the
          stepper — same copy, same shell, same rail on both now. */}
      {/* Gate widened 2026-08-09: (a) `isAuthenticated` instead of
          `returnedFromAuth` so a signed-in user typing a code manually gets
          the same clear takeover during the seconds-long redemption (RBAC
          bootstrap + welcome seeding) instead of just a button-label change;
          (b) `authReturnBoot` covers the first paint after auth return,
          before the auto-redeem effect fires. */}
      {(((codeLoading || codeSuccess) && isAuthenticated)
        || (authReturnBoot && !codeError && !codeSuccess && !codeLoading)) && (
        <FocusedRedeemShell
          rail={<JourneyStepper currentKey={codeSuccess ? 'profile' : 'account'} railOnDesktop />}
        >
          <RedeemProgressPanel stage={codeSuccess ? 'applied' : 'applying'} />
        </FocusedRedeemShell>
      )}

      <SeoHead
        title="Start Your Trial — prest0"
        description="Get started with prest0 — activate your access code and put an AI employee to work for your team, with PRO Work Units included from day one."
        canonicalUrl="https://prest0.ai/start"
        keywords="prest0 trial, AI employee trial, enterprise AI trial, AI assistant for teams, legal AI trial, finance AI trial"
      />
      {/* Desktop nav */}
      <HomeHeader>
        
        <HeaderIconLink to="/team">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          team
        </HeaderIconLink>
        <HeaderIconLink to="/mission">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
            <line x1="4" y1="22" x2="4" y2="15"/>
          </svg>
          mission
        </HeaderIconLink>
        <HeaderIconLink to="/pricing">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="1" x2="12" y2="23"/>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
          pricing
        </HeaderIconLink>
        <HeaderIconLink to="/">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          home
        </HeaderIconLink>
        {isAuthenticated ? (
          <>
            <NotificationsBell />
            <ProfileImage size={36} />
          </>
        ) : (
          <HeaderIconButton onClick={handleLogin}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            login
          </HeaderIconButton>
        )}
      </HomeHeader>

      {/* Mobile menu toggle */}
      <MobileMenuToggle
        onClick={() => setMenuOpen(!menuOpen)}
        data-mobile-toggle="true"
        aria-label="Toggle menu"
      >
        <MobileMenuIcon isOpen={menuOpen}>
          <span></span>
          <span></span>
          <span></span>
        </MobileMenuIcon>
      </MobileMenuToggle>

      <MobileMenuDropdown isOpen={menuOpen} ref={mobileMenuRef}>
        
        <MobileMenuLink to="/team" onClick={() => setMenuOpen(false)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          team
        </MobileMenuLink>
        <MobileMenuLink to="/mission" onClick={() => setMenuOpen(false)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
            <line x1="4" y1="22" x2="4" y2="15"/>
          </svg>
          mission
        </MobileMenuLink>
        <MobileMenuLink to="/pricing" onClick={() => setMenuOpen(false)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="1" x2="12" y2="23"/>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
          pricing
        </MobileMenuLink>
        <MobileMenuLink to="/" onClick={() => setMenuOpen(false)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          home
        </MobileMenuLink>
        {isAuthenticated ? (
          <>
            <MobileMenuLink to="/desktop" onClick={() => setMenuOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
              desktop
            </MobileMenuLink>
            {/* ORG-USAGE-2026: same billing.view gate as the header nav link —
                fail-open (guests / legacy accounts unaffected), so a member the
                admin restricted doesn't get a mobile-menu path to the access wall. */}
            {canSeeWallet && (
              <MobileMenuLink to="/wallet" onClick={() => setMenuOpen(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                wallet
              </MobileMenuLink>
            )}
            {/* ORG-USAGE-2026: same activity.view gate as the header nav link —
                fail-open. Menu order convention: activity sits between wallet
                and profile. */}
            {canSeeActivity && (
              <MobileMenuLink to="/activity" onClick={() => setMenuOpen(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18" />
                  <path d="M7 15l4-4 3 3 5-6" />
                </svg>
                activity
              </MobileMenuLink>
            )}
            <MobileMenuLink to="/profile" onClick={() => setMenuOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              profile
            </MobileMenuLink>
            <MobileNotifications closeMenu={() => setMenuOpen(false)} />
            <MobileMenuButton onClick={() => logout()} style={{ color: '#ff4d4f' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              logout
            </MobileMenuButton>
          </>
        ) : (
          <MobileMenuButton onClick={handleLogin}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            login
          </MobileMenuButton>
        )}
      </MobileMenuDropdown>

      {/* Hero */}
      <Hero>
        <Eyebrow>PREST0 — YOUR AI EMPLOYEE</Eyebrow>
        <HeroTitle>Start your evaluation</HeroTitle>
        <HeroSub>
          Prest0 is invite-only. Enter the invitation code our team gave you to activate
          your private workspace — PRO Work Units loaded on day one, no credit card required.
        </HeroSub>
      </Hero>

      {/* Code input — or the follow-up panels for the mode-based flows */}
      <CodeSection>
        {redeemOutcome ? (
          <FocusedRedeemShell>
            <RedemptionOutcomePanel
              outcome={redeemOutcome}
              onGoToWorkspace={() => navigate('/desktop')}
              onGoToWallet={() => navigate('/wallet')}
            />
          </FocusedRedeemShell>
        ) : accountQuestionCode ? (
          <FocusedRedeemShell
            rail={<JourneyStepper currentKey="account" railOnDesktop />}
            onBack={() => {
            try { sessionStorage.removeItem('pending_trial_code'); } catch {}
            setAccountQuestionCode(null);
          }}>
            <AccountQuestionPanel
              onSignIn={() => {
                try { sessionStorage.setItem('pending_trial_code', accountQuestionCode); } catch {}
                navigate('/auth', {
                  state: {
                    returnUrl: `/start?code=${encodeURIComponent(accountQuestionCode)}`,
                    authMode: 'login',
                  },
                });
              }}
              onCreateAccount={() => {
                try { sessionStorage.setItem('pending_trial_code', accountQuestionCode); } catch {}
                navigate('/auth', {
                  state: {
                    returnUrl: `/start?code=${encodeURIComponent(accountQuestionCode)}`,
                    authMode: 'signup',
                  },
                });
              }}
            />
          </FocusedRedeemShell>
        ) : (
        <>
        <BigCodeInputRow>
          <BigCodeInput
            type="text"
            placeholder="PREST0-XXXX-XXXX"
            value={codeInput}
            onChange={(e) => {
              setCodeInput(e.target.value.toUpperCase());
              setCodeError(null);
            }}
            onKeyDown={handleCodeKeyDown}
            disabled={codeLoading || codeSuccess}
            $pulsed={prefilledFromUrl && !!codeInput}
            aria-label="Access code"
          />
          <BigCodeButton
            ref={activateButtonRef}
            onClick={handleRedeemCode}
            disabled={codeLoading || codeSuccess || !codeInput.trim()}
          >
            {codeLoading ? 'Checking…' : codeSuccess ? 'Redirecting…' : 'Activate Trial'}
          </BigCodeButton>
        </BigCodeInputRow>
        {codeError && <CodeError>{codeError}</CodeError>}
        {codeSuccess && (
          <CodeSuccess>{codeSuccessMessage}</CodeSuccess>
        )}
        {returnedFromAuth && !codeSuccess && !codeError && !codeLoading && (
          <CodeSuccess>Account ready — activating your trial…</CodeSuccess>
        )}
        </>
        )}
      </CodeSection>

      {/* Trust signals */}
      <TrustRow>
        {TRUST_SIGNALS.map((s) => (
          <TrustPill key={s.label}>
            <TrustDot />
            {s.label}
          </TrustPill>
        ))}
      </TrustRow>

      {/* Capabilities — Live today */}
      <CapSection>
        <CapHeading>Everything Prest0 can do today</CapHeading>
        <CapGrid>
          {LIVE_CAPABILITIES.map((cap) => (
            <CapCard key={cap.title}>
              <CapIcon>{cap.icon}</CapIcon>
              <CapTitle>{cap.title}</CapTitle>
              <CapDesc>{cap.desc}</CapDesc>
            </CapCard>
          ))}
        </CapGrid>

        {/* Capabilities — Buildable by the team */}
        <CapSubheading>What your team can build on Prest0</CapSubheading>
        <BuildableGrid>
          {BUILDABLE_CAPABILITIES.map((cap) => (
            <BuildableCard key={cap.title}>
              <CapIcon>{cap.icon}</CapIcon>
              <CapTitle>{cap.title}</CapTitle>
              <CapDesc>{cap.desc}</CapDesc>
            </BuildableCard>
          ))}
        </BuildableGrid>
      </CapSection>

      {/* Footer */}
      <FooterNote>
        Questions? Ask right here in the chat{' '}·{' '}
        <Link to="/pricing" style={{ color: '#888', textDecoration: 'underline' }}>
          View full pricing details
        </Link>
      </FooterNote>

      {/* Standard site footer — same treatment as /pricing (2026-08-03): its
          tall bottom padding also keeps the floating chat bar off the last
          readable content at full scroll. */}
      <GlobalFooter />
    </Page>
  );
};

export default StartTrial;
